/**
 * The series a "total" chart draws: a cumulative register read at intervals, cut
 * into the intervals it is charted over.
 *
 * A counter log row is a *reading of a running total* (`counter_value` 22540.9,
 * then 22541.0, …). The energy of an interval is the change between its first and
 * last reading — never the reading itself, and never the sum of the readings.
 * Charting the rows raw is what produced a wall of zero bars (the log's own
 * `delta` column is usually NULL) and, where the row was treated as a value, a
 * few absurdly tall bars (sums of a 22540 kWh register).
 *
 * So both surfaces that chart a register — the chart modal and the energy page —
 * go through here: one place decides the interval width, one place turns readings
 * into per-interval totals, and empty intervals stay `null` instead of drawing a
 * confident zero.
 */
import { formatDateTime } from './formatDate';

/** One row of `/values/:id/history`, as either logging type hands it over. */
export interface HistoryRow {
  timestamp: string;
  value?: number | null;
  avg?: number | null;
  delta?: number | null;
}

/** An interval's start and the total it holds (`null` when it holds no reading). */
export interface IntervalTotal {
  timestamp: number;
  total: number | null;
}

/** The bar grid a chart draws: `count` intervals ending at `end`. */
export interface IntervalGrid {
  /** The last interval's start, ms — usually "now", the same edge the read used. */
  end: number;
  /** How many intervals the period draws, so 30d is 30 bars, not "as many as data". */
  count: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The bar width a period draws. Both vocabularies are accepted — the chart modal
 * picks `1h…30d`, the energy page `day…year` — so the two surfaces can never drift
 * into drawing the same period at different widths.
 */
export function historyBucketMs(period: string): number {
  switch (period) {
    case '1h':
    case 'hour':
      return 5 * MINUTE;
    case '6h':
      return 15 * MINUTE;
    case '24h':
    case 'day':
      return HOUR;
    case '7d':
    case 'week':
      return 6 * HOUR;
    case '30d':
    case 'month':
      return DAY;
    case 'year':
      return 7 * DAY;
    default:
      return HOUR;
  }
}

/**
 * How many bars a period draws — 30d is thirty bars, whatever the log holds.
 *
 * Without this the grid came from the rows' own span, so a hub that had been
 * logging for one day answered a 30d request with a single bar holding the whole
 * day's total.
 */
export function intervalsForPeriod(period: string): number {
  switch (period) {
    case '1h':
      return 12;
    case '6h':
      return 24;
    case '24h':
    case 'day':
      return 24;
    case '7d':
    case 'week':
      return 28;
    case '30d':
    case 'month':
      return 30;
    case 'year':
      return 52;
    default:
      return 24;
  }
}

/** The interval's word, for the legend: a bar is one of these, not one reading. */
export function intervalWord(period: string): { en: string; sl: string } {
  switch (historyBucketMs(period)) {
    case 5 * MINUTE:
      return { en: 'per 5 min', sl: 'na 5 min' };
    case 15 * MINUTE:
      return { en: 'per 15 min', sl: 'na 15 min' };
    case HOUR:
      return { en: 'per hour', sl: 'na uro' };
    case 6 * HOUR:
      return { en: 'per 6 hours', sl: 'na 6 ur' };
    case DAY:
      return { en: 'per day', sl: 'na dan' };
    default:
      return { en: 'per week', sl: 'na teden' };
  }
}

/** A reading's timestamp in ms, or `null` when the row is not a usable one. */
function timeOf(row: HistoryRow): number | null {
  const timestamp = Date.parse(row.timestamp);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/** A finite number, or `null` — `NaN` from an empty JSON value is not a reading. */
function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * A cumulative register's per-interval totals.
 *
 * The first reading has no change to report, so it seeds the running total. A
 * register that goes *backwards* (a meter swap, a counter reset) reports the
 * change it can see and never a negative one.
 *
 * With a `grid` the series is exactly `grid.count` intervals ending at
 * `grid.end` — the period the reader asked for (30d is thirty daily bars) — so a
 * log that only covers today still draws a month of slots, one filled. Changes
 * outside the grid are not drawn.
 *
 * Without one the series spans the rows themselves, interval by interval. Either
 * way an interval with no change is `null`: a gap in the log reads as a gap in
 * the chart, never as a zero.
 */
export function cumulativeIntervals(
  rows: readonly HistoryRow[],
  bucketMs: number,
  grid?: IntervalGrid,
): IntervalTotal[] {
  if (!Number.isFinite(bucketMs) || bucketMs <= 0) return [];

  const readings = rows
    .map((row) => ({ timestamp: timeOf(row), value: numberOrNull(row.value), delta: numberOrNull(row.delta) }))
    .filter((row): row is { timestamp: number; value: number | null; delta: number | null } => row.timestamp !== null)
    .sort((left, right) => left.timestamp - right.timestamp);

  if (readings.length === 0 && !grid) return [];

  const perBucket = new Map<number, number>();
  let previous: number | null = null;

  for (const reading of readings) {
    let change: number | null = null;
    if (reading.delta !== null) {
      change = reading.delta;
      previous = reading.value ?? previous;
    } else if (reading.value !== null && previous !== null) {
      change = Math.max(0, reading.value - previous);
      previous = reading.value;
    } else if (reading.value !== null) {
      // The first reading of the range: it seeds the total, it is not a change.
      previous = reading.value;
    }

    if (change === null) continue;
    const bucket = Math.floor(reading.timestamp / bucketMs) * bucketMs;
    perBucket.set(bucket, (perBucket.get(bucket) ?? 0) + change);
  }

  const starts = grid
    ? gridStarts(grid, bucketMs)
    : spanStarts(readings[0]?.timestamp, readings[readings.length - 1]?.timestamp, bucketMs);

  return starts.map((timestamp) => ({ timestamp, total: perBucket.get(timestamp) ?? null }));
}

/** The period's own slots, ending with the one that holds `grid.end`. */
function gridStarts(grid: IntervalGrid, bucketMs: number): number[] {
  const count = Math.max(1, Math.floor(grid.count));
  const end = Math.floor(grid.end / bucketMs) * bucketMs;
  const starts: number[] = [];
  for (let index = count - 1; index >= 0; index -= 1) {
    starts.push(end - index * bucketMs);
  }
  return starts;
}

/** The slots the readings themselves cover, first to last. */
function spanStarts(first: number | undefined, last: number | undefined, bucketMs: number): number[] {
  if (first === undefined || last === undefined) return [];
  const from = Math.floor(first / bucketMs) * bucketMs;
  const to = Math.floor(last / bucketMs) * bucketMs;
  const starts: number[] = [];
  for (let timestamp = from; timestamp <= to; timestamp += bucketMs) {
    starts.push(timestamp);
  }
  return starts;
}

/** An interval's label on the x-axis, at the resolution that period reads at. */
export function intervalLabel(timestamp: number, period: string): string {
  const date = new Date(timestamp);
  if (period === '7d' || period === 'week') {
    return formatDateTime(date, { month: 'short', day: 'numeric', hour: '2-digit' });
  }
  if (period === '30d' || period === 'month' || period === 'year') {
    return formatDateTime(date, { month: 'short', day: 'numeric' });
  }
  return formatDateTime(date, { hour: '2-digit', minute: '2-digit' });
}
